import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback } from 'react'

import { post, get } from '@/lib/api'

import type { ExportType, ExportFormat, ExportStatus } from '@company/shared'


// ==================== Types ====================

export type { ExportType, ExportFormat, ExportStatus }

export interface ExportJob {
  id: string
  userId: string
  type: ExportType
  filters: string
  format: ExportFormat
  status: ExportStatus
  fileKey: string | null
  fileUrl: string | null
  errorMessage: string | null
  expiresAt: string | null
  createdAt: string
  completedAt: string | null
}

export interface CreateExportInput {
  type: ExportType
  filters: Record<string, unknown>
  format?: ExportFormat
}

// ==================== Query Keys ====================

export const exportKeys = {
  all: ['exports'] as const,
  lists: () => [...exportKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...exportKeys.lists(), params] as const,
  detail: (id: string) => [...exportKeys.all, 'detail', id] as const,
}

// ==================== Hooks ====================

/**
 * 创建导出任务
 */
export function useCreateExport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateExportInput) => {
      return post<ExportJob>('/exports', input)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exportKeys.lists() })
    },
  })
}

/**
 * 获取导出任务详情
 */
export function useExportJob(id: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: exportKeys.detail(id ?? ''),
    queryFn: async () => {
      if (!id) {return null}
      return get<ExportJob>(`/exports/${id}`)
    },
    enabled: !!id && (options?.enabled ?? true),
  })
}

/**
 * 获取导出任务列表
 */
export function useExportJobs(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: exportKeys.list(params ?? {}),
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params?.page) {searchParams.set('page', params.page.toString())}
      if (params?.pageSize) {searchParams.set('pageSize', params.pageSize.toString())}
      const query = searchParams.toString()
      return get<ExportJob[]>(`/exports${query ? `?${query}` : ''}`)
    },
  })
}

/**
 * 导出任务状态轮询 Hook
 * 自动轮询任务状态直到完成或失败
 */
export function useExportWithPolling(options?: {
  onSuccess?: (job: ExportJob) => void
  onError?: (error: string) => void
  pollInterval?: number
}) {
  const [jobId, setJobId] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const queryClient = useQueryClient()

  const createExport = useCreateExport()

  // 轮询任务状态
  const { data: job } = useExportJob(jobId, {
    enabled: isPolling && !!jobId,
  })

  // 监听任务状态变化
  useEffect(() => {
    if (!job || !isPolling) {return}

    if (job.status === 'completed') {
      setIsPolling(false)
      options?.onSuccess?.(job)
    } else if (job.status === 'failed') {
      setIsPolling(false)
      options?.onError?.(job.errorMessage ?? '导出失败')
    }
  }, [job, isPolling, options])

  // 轮询定时器
  useEffect(() => {
    if (!isPolling || !jobId) {return}

    const interval = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: exportKeys.detail(jobId) })
    }, options?.pollInterval ?? 2000)

    return () => clearInterval(interval)
  }, [isPolling, jobId, queryClient, options?.pollInterval])

  const startExport = useCallback(
    async (input: CreateExportInput) => {
      try {
        const newJob = await createExport.mutateAsync(input)
        setJobId(newJob.id)
        setIsPolling(true)
        return newJob
      } catch (error) {
        options?.onError?.(error instanceof Error ? error.message : '创建导出任务失败')
        throw error
      }
    },
    [createExport, options]
  )

  const reset = useCallback(() => {
    setJobId(null)
    setIsPolling(false)
  }, [])

  return {
    startExport,
    reset,
    job,
    jobId,
    isPolling,
    isCreating: createExport.isPending,
    status: job?.status ?? (createExport.isPending ? 'pending' : null),
  }
}

/**
 * 获取导出文件下载 URL
 */
export function getExportDownloadUrl(jobId: string): string {
  return `/api/v1/exports/${jobId}/download`
}
